import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check, ClipboardList, ChevronDown, ChevronRight } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { useActiveTenant } from '../contexts/tenant'
import { useAuth } from '../contexts/auth'

type Discrepancy = {
  id: string; productId: string; sku: string | null; productName: string | null
  countedUnits: number; systemUnits: number; diff: number
  status: string; scope: string | null; notes: string | null; createdAt: string; resolvedAt: string | null
}

type Sessao = {
  id: string; scope: string | null; mode: string
  checked: number; divergent: number; matched: number
  createdAt: string; userEmail: string | null; userName: string | null
}
type SessaoItem = {
  productId: string; sku: string | null; name: string | null
  countedUnits: number; systemUnits: number; diff: number; locationLabel: string | null
}

const scopeLabel = (s: string | null) =>
  s === 'fracionamento' ? 'Fracionamento' : s === 'armazem' ? 'Armazém' : 'Combinada'

const statusLabel = (s: string) =>
  s === 'open' ? 'Aberta' : s === 'resolved' ? 'Resolvida' : s === 'superseded' ? 'Reconferida' : s

const fmt = (d: string) => new Date(d).toLocaleString('pt-BR')

export function DivergenciasPage() {
  const tenantId = useActiveTenant()
  const { user } = useAuth()
  const qc = useQueryClient()
  const canResolve = user?.role === 'admin' || user?.role === 'operator'
  const [status, setStatus] = useState<'open' | 'all'>('open')
  const [scope, setScope] = useState<'all' | 'fracionamento' | 'armazem' | 'combinada'>('all')
  const [resolving, setResolving] = useState<string | null>(null)
  const [aberta, setAberta] = useState<string | null>(null) // conferência expandida

  // Conferências feitas — o registro existe mesmo quando bateu tudo.
  const { data: sessoes = [] } = useQuery<Sessao[]>({
    queryKey: ['conferences', tenantId, scope],
    queryFn: async () => (await api.get('/stock/conferences', {
      params: { tenantId, ...(scope !== 'all' && scope !== 'combinada' ? { scope } : {}) },
    })).data,
    enabled: !!tenantId,
  })

  const { data: detalhe } = useQuery<Sessao & { items: SessaoItem[] }>({
    queryKey: ['conference', aberta],
    queryFn: async () => (await api.get(`/stock/conferences/${aberta}`)).data,
    enabled: !!aberta,
  })

  const { data: rows = [], isLoading } = useQuery<Discrepancy[]>({
    queryKey: ['discrepancies', tenantId, status, scope],
    queryFn: async () => (await api.get('/stock/discrepancies', { params: { tenantId, status, ...(scope !== 'all' ? { scope } : {}) } })).data,
    enabled: !!tenantId,
  })

  async function resolve(id: string) {
    setResolving(id)
    try {
      await api.patch(`/stock/discrepancies/${id}/resolve`)
      await qc.invalidateQueries({ queryKey: ['discrepancies', tenantId] })
    } finally {
      setResolving(null)
    }
  }


  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  const openCount = rows.filter(r => r.status === 'open').length

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Divergências de contagem</h2>
          <p className="text-sm text-gray-500">Diferenças entre a <b>conferência</b> e o saldo do sistema. Não alteram estoque — são alertas para revisar.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
            {(['all', 'fracionamento', 'armazem', 'combinada'] as const).map(s => (
              <button key={s} onClick={() => setScope(s)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  scope === s ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}>
                {s === 'all' ? 'Todos escopos' : s === 'fracionamento' ? 'Fracionamento' : s === 'armazem' ? 'Armazém' : 'Combinada'}
              </button>
            ))}
          </div>
          <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
            {(['open', 'all'] as const).map(s => (
              <button key={s} onClick={() => setStatus(s)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  status === s ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}>
                {s === 'open' ? 'Abertas' : 'Todas'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : rows.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <Check className="mx-auto mb-2 h-8 w-8 text-green-500" />
              <p className="text-sm text-gray-500">{status === 'open' ? 'Nenhuma divergência aberta — tudo conferido.' : 'Nenhuma divergência registrada.'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">SKU</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Produto</th>
                    <th className="px-5 py-3 text-right font-medium text-gray-600">Contado</th>
                    <th className="px-5 py-3 text-right font-medium text-gray-600">Sistema</th>
                    <th className="px-5 py-3 text-right font-medium text-gray-600">Diferença</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Escopo</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Quando</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map(r => (
                    <tr key={r.id} className={`hover:bg-gray-50 ${r.status === 'open' ? 'bg-amber-50/40' : ''}`}>
                      <td className="px-5 py-3 font-mono font-medium text-gray-900">{r.sku ?? '—'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.productName ?? '—'}</td>
                      <td className="px-5 py-3 text-right text-gray-700">{r.countedUnits}</td>
                      <td className="px-5 py-3 text-right text-gray-700">{r.systemUnits}</td>
                      <td className={`px-5 py-3 text-right font-bold ${r.diff > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {r.diff > 0 ? '+' : ''}{r.diff}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                          r.scope === 'armazem' ? 'bg-blue-50 text-blue-700'
                            : r.scope === 'fracionamento' ? 'bg-amber-50 text-amber-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}>{scopeLabel(r.scope)}</span>
                      </td>
                      <td className="px-5 py-3 text-gray-500">{new Date(r.createdAt).toLocaleString('pt-BR')}</td>
                      <td className="px-5 py-3">
                        {r.status === 'open'
                          ? <Badge variant="warning"><AlertTriangle className="mr-1 inline h-3 w-3" />Aberta</Badge>
                          : <Badge variant={r.status === 'superseded' ? 'default' : 'success'}>{statusLabel(r.status)}</Badge>}
                      </td>
                      <td className="px-5 py-3 text-right">
                        {r.status === 'open' && canResolve && (
                          <Button variant="secondary" size="sm" loading={resolving === r.id} onClick={() => resolve(r.id)}>
                            <Check className="h-4 w-4" /> Resolver
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {openCount > 0 && (
        <p className="text-xs text-gray-400">
          "Resolver" apenas marca a divergência como tratada (não altera estoque). Para corrigir o saldo de verdade,
          use a <b>Contagem</b> (admin), os <b>Ajustes</b> ou a correção de <b>Paletes</b>.
        </p>
      )}

      {/* Conferências feitas — o registro existe mesmo quando bateu tudo. Sem ele
          não havia como responder "foi conferido ontem? por quem? o que contaram?" */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3">
            <ClipboardList className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-semibold text-gray-900">Conferências realizadas</span>
            <span className="text-xs text-gray-400">{sessoes.length} registro(s) · clique para ver o que foi contado</span>
          </div>

          {sessoes.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">
              Nenhuma conferência registrada ainda. As próximas ficam aqui, com ou sem divergência.
            </p>
          ) : (
            <div className="divide-y divide-gray-100">
              {sessoes.map(se => (
                <div key={se.id}>
                  <button onClick={() => setAberta(aberta === se.id ? null : se.id)}
                    className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-gray-50">
                    {aberta === se.id ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                    <span className="w-40 shrink-0 text-sm text-gray-600">{fmt(se.createdAt)}</span>
                    <Badge variant="default">{scopeLabel(se.scope)}</Badge>
                    <span className="flex-1 truncate text-sm text-gray-700">{se.userName ?? se.userEmail ?? '—'}</span>
                    <span className="text-sm text-gray-500">{se.checked} SKU(s)</span>
                    {se.divergent > 0
                      ? <Badge variant="warning">{se.divergent} divergente(s)</Badge>
                      : <Badge variant="success">tudo bateu</Badge>}
                  </button>

                  {aberta === se.id && (
                    <div className="overflow-x-auto bg-gray-50 px-5 py-3">
                      {!detalhe ? (
                        <p className="text-sm text-gray-400">Carregando...</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-gray-500">
                              <th className="py-1 pr-4">SKU</th>
                              <th className="py-1 pr-4">Produto</th>
                              <th className="py-1 pr-4">Onde</th>
                              <th className="py-1 pr-4 text-right">Contado</th>
                              <th className="py-1 pr-4 text-right">Sistema</th>
                              <th className="py-1 text-right">Dif.</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {detalhe.items.map(it => (
                              <tr key={it.productId}>
                                <td className="py-1.5 pr-4 font-mono text-xs text-gray-500">{it.sku ?? '—'}</td>
                                <td className="py-1.5 pr-4 text-gray-700">{it.name ?? '—'}</td>
                                <td className="py-1.5 pr-4 text-xs text-gray-500">{it.locationLabel ?? '—'}</td>
                                <td className="py-1.5 pr-4 text-right font-medium">{it.countedUnits}</td>
                                <td className="py-1.5 pr-4 text-right text-gray-500">{it.systemUnits}</td>
                                <td className={`py-1.5 text-right font-semibold ${it.diff === 0 ? 'text-gray-400' : it.diff > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                  {it.diff > 0 ? `+${it.diff}` : it.diff}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
